import debugx from "debugx";

const debug = debugx("bikedash:compass");

interface CompassHeadingOptions {
  smoothingFactor?: number; // 0-1, higher = faster response
  onUpdate: (heading: number) => void;
}

export class CompassHeading {
  private latestRawHeading: number | null = null;
  private smoothedHeading: number = 0;
  private animationFrameId: number | null = null;
  private options: CompassHeadingOptions;
  private isActive: boolean = false;

  constructor(options: CompassHeadingOptions) {
    this.options = {
      smoothingFactor: 0.15,
      ...options,
    };
  }

  start() {
    if (this.isActive) return;
    this.isActive = true;

    // Animation loop for smooth compass updates
    const animate = () => {
      if (!this.isActive) return;

      if (this.latestRawHeading !== null) {
        // Calculate shortest angle difference
        const diff =
          ((this.latestRawHeading - this.smoothedHeading + 540) % 360) - 180;

        // Apply smoothing
        this.smoothedHeading += diff * this.options.smoothingFactor!;

        // Normalize to 0-360
        this.smoothedHeading = ((this.smoothedHeading % 360) + 360) % 360;

        this.options.onUpdate(this.smoothedHeading);
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };

    this.animationFrameId = requestAnimationFrame(animate);

    // Try modern AbsoluteOrientationSensor API first (uses quaternions, avoids gimbal lock)
    if (
      typeof (window as any).AbsoluteOrientationSensor !== "undefined" &&
      !("webkitCompassHeading" in ({} as any))
    ) {
      debug("Attempting to use AbsoluteOrientationSensor (quaternion-based)");
      this.startAbsoluteOrientationSensor();
      return;
    }

    // Fall back to DeviceOrientation API
    this.startDeviceOrientation();
  }

  stop() {
    this.isActive = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Remove all event listeners
    window.removeEventListener("deviceorientationabsolute", () => {});
    window.removeEventListener("deviceorientation", () => {});
  }

  updateRawHeading(heading: number) {
    this.latestRawHeading = heading;
  }

  private async startAbsoluteOrientationSensor() {
    try {
      const sensor = new (window as any).AbsoluteOrientationSensor({
        frequency: 60,
        referenceFrame: "device",
      });

      sensor.addEventListener("reading", () => {
        const q = sensor.quaternion; // [x, y, z, w]
        if (!q || q.length !== 4) return;

        // Extract heading from quaternion (yaw angle around Z axis)
        // This formula extracts the compass heading while avoiding gimbal lock
        const [x, y, z, w] = q;

        // Calculate yaw (rotation around vertical axis)
        const heading = Math.atan2(
          2 * (w * z + x * y),
          1 - 2 * (y * y + z * z),
        );

        // Convert to degrees and normalize to 0-360
        let headingDegrees = (heading * 180) / Math.PI;
        if (headingDegrees < 0) headingDegrees += 360;

        // Invert direction (360 - heading) so turning left increases heading
        headingDegrees = (360 - headingDegrees) % 360;

        this.latestRawHeading = headingDegrees;
      });

      sensor.addEventListener("error", (event: any) => {
        debug("AbsoluteOrientationSensor error:", event.error);
        // Fall back to DeviceOrientation
        this.startDeviceOrientation();
      });

      sensor.start();
      debug("AbsoluteOrientationSensor started successfully");
    } catch (error) {
      debug("Failed to start AbsoluteOrientationSensor:", error);
      // Fall back to DeviceOrientation
      this.startDeviceOrientation();
    }
  }

  private startDeviceOrientation() {
    let rawUpdateCount = 0;
    let lastRawSecond = Date.now();

    const handleCompassEvent = (event: any) => {
      let rawHeading: number | null = null;

      if ("webkitCompassHeading" in event) {
        // iOS Safari method (already tilt-compensated)
        rawHeading = event.webkitCompassHeading;
      } else if (
        event.absolute &&
        event.alpha !== null &&
        event.beta !== null &&
        event.gamma !== null
      ) {
        // Standard deviceorientation method (Android Chrome)
        // Apply proper tilt compensation using rotation matrix
        const alpha = event.alpha;
        const beta = event.beta;
        const gamma = event.gamma;

        // Check for gimbal lock - when beta approaches ±90°, the compass calculation becomes unstable
        // Ignore readings when device is nearly vertical (tilted forward/back > 75°)
        if (Math.abs(beta) > 75) {
          // Skip this reading - gimbal lock zone
          // Keep using the last valid heading (don't update rawHeading)
          return;
        }

        // Convert degrees to radians
        const alphaRad = (alpha * Math.PI) / 180;
        const betaRad = (beta * Math.PI) / 180;
        const gammaRad = (gamma * Math.PI) / 180;

        // Calculate equation components
        const cA = Math.cos(alphaRad);
        const sA = Math.sin(alphaRad);
        const cB = Math.cos(betaRad);
        const sB = Math.sin(betaRad);
        const cG = Math.cos(gammaRad);
        const sG = Math.sin(gammaRad);

        // Calculate rotation components (rotation matrix for tilt compensation)
        const rA = -cA * sG - sA * sB * cG;
        const rB = -sA * sG + cA * sB * cG;

        // Calculate compass heading using atan2 (handles all quadrants correctly)
        let compassHeading = Math.atan2(rA, rB);

        // Convert from half unit circle to whole unit circle
        if (rB < 0) {
          compassHeading += Math.PI;
        } else if (rA < 0) {
          compassHeading += 2 * Math.PI;
        }

        // Convert radians to degrees and invert direction
        rawHeading = (compassHeading * 180) / Math.PI;

        // Invert direction (360 - heading) so turning left increases heading
        rawHeading = (360 - rawHeading) % 360;
      }

      if (rawHeading !== null && !isNaN(rawHeading)) {
        rawUpdateCount++;
        const now = Date.now();

        // Log raw compass event rate
        if (now - lastRawSecond >= 1000) {
          debug("RAW COMPASS EVENT RATE: %s/sec", rawUpdateCount);
          rawUpdateCount = 0;
          lastRawSecond = now;
        }

        this.latestRawHeading = ((rawHeading % 360) + 360) % 360;
      }
    };

    const startCompass = () => {
      debug("Starting DeviceOrientation event listeners");
      // Check for deviceorientationabsolute first (most reliable)
      if ("ondeviceorientationabsolute" in window) {
        window.addEventListener(
          "deviceorientationabsolute",
          handleCompassEvent,
          true,
        );
      } else if ("ondeviceorientation" in window) {
        (window as any).addEventListener(
          "deviceorientation",
          handleCompassEvent,
          true,
        );
      } else {
        debug("Device orientation not supported");
      }
    };

    // Check if we need permission (iOS 13+)
    if (
      typeof (DeviceOrientationEvent as any).requestPermission === "function"
    ) {
      // For iOS, request permission on first user interaction
      const handleInteraction = async () => {
        try {
          const response = await (
            DeviceOrientationEvent as any
          ).requestPermission();
          if (response === "granted") {
            startCompass();
          }
        } catch (error) {
          debug("Error requesting device orientation permission:", error);
        }
        document.removeEventListener("click", handleInteraction);
      };
      document.addEventListener("click", handleInteraction);
    } else {
      // Start immediately for other browsers
      startCompass();
    }
  }
}
